(() => {
  const DISMISSED_KEY = 'noxtek_welcome_popup_dismissed';
  const SUBSCRIBED_KEY = 'noxtek_welcome_popup_subscribed';
  const SESSION_KEY = 'noxtek_welcome_popup_session_shown';
  const RESPONSE_HASH = '#noxtek-welcome-popup';

  class NoxTekWelcomePopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-noxtek-submit]');
      this.closeButtons = [...this.querySelectorAll('[data-noxtek-popup-close]')];
      this.delaySeconds = this.readNumber('delaySeconds', 12);
      this.scrollPercent = this.readNumber('scrollPercent', 35);
      this.dismissDays = this.readNumber('dismissDays', 14);
      this.subscribedDays = this.readNumber('subscribedDays', 90);
      this.designMode = this.dataset.designMode === 'true';
      this.hasSuccess = Boolean(this.querySelector('[data-noxtek-popup-success]'));
      this.hasErrors = Boolean(this.querySelector('[data-noxtek-popup-errors]'));
      this.opened = false;
      this.previousFocus = null;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close(true));
      });

      this.form?.addEventListener('submit', this.handleSubmit);
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);

      const returnedFromPopup = window.location.hash === RESPONSE_HASH;

      if (returnedFromPopup && (this.hasSuccess || this.hasErrors)) {
        if (this.hasSuccess) {
          this.writeExpiry(SUBSCRIBED_KEY, this.subscribedDays);
          this.removeStorage(DISMISSED_KEY);
        }
        this.open({ rememberSession: true });
        return;
      }

      if (this.designMode) return;
      if (this.isExpiryActive(SUBSCRIBED_KEY) || this.isExpiryActive(DISMISSED_KEY) || this.sessionWasShown()) return;

      this.delayTimer = window.setTimeout(() => this.trigger(), this.delaySeconds * 1000);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    disconnectedCallback() {
      window.clearTimeout(this.delayTimer);
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.form?.removeEventListener('submit', this.handleSubmit);
    }

    readNumber(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    getStorage(type = 'local') {
      try {
        const storage = type === 'session' ? window.sessionStorage : window.localStorage;
        const probe = `noxtek_popup_probe_${type}`;
        storage.setItem(probe, '1');
        storage.removeItem(probe);
        return storage;
      } catch (_error) {
        return null;
      }
    }

    isExpiryActive(key) {
      const storage = this.getStorage();
      if (!storage) return false;

      try {
        const raw = storage.getItem(key);
        if (!raw) return false;
        const record = JSON.parse(raw);
        const expiresAt = Date.parse(record.expiresAt);

        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
          storage.removeItem(key);
          return false;
        }

        return true;
      } catch (_error) {
        try {
          storage.removeItem(key);
        } catch (_storageError) {
          // Storage is optional; the popup remains functional without it.
        }
        return false;
      }
    }

    writeExpiry(key, days) {
      const storage = this.getStorage();
      if (!storage) return;

      try {
        storage.setItem(
          key,
          JSON.stringify({
            expiresAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
          })
        );
      } catch (_error) {
        // Storage is optional; never block the native Shopify form.
      }
    }

    removeStorage(key) {
      try {
        this.getStorage()?.removeItem(key);
      } catch (_error) {
        // No action required when storage is unavailable.
      }
    }

    sessionWasShown() {
      try {
        return this.getStorage('session')?.getItem(SESSION_KEY) === 'true';
      } catch (_error) {
        return false;
      }
    }

    rememberSession() {
      try {
        this.getStorage('session')?.setItem(SESSION_KEY, 'true');
      } catch (_error) {
        // A page-level flag still prevents duplicate opening.
      }
    }

    handleScroll() {
      if (this.opened) return;
      const scrollableHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const progress = (window.scrollY / scrollableHeight) * 100;

      if (progress >= this.scrollPercent) this.trigger();
    }

    trigger() {
      if (this.opened || this.sessionWasShown()) return;
      window.clearTimeout(this.delayTimer);
      window.removeEventListener('scroll', this.handleScroll);
      this.open({ rememberSession: true });
    }

    open({ rememberSession = false } = {}) {
      if (this.opened) return;
      this.opened = true;
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (rememberSession) this.rememberSession();
      this.hidden = false;
      document.documentElement.classList.add('noxtek-welcome-popup-open');
      document.body.classList.add('noxtek-welcome-popup-open');
      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        const focusTarget = this.querySelector('[data-noxtek-initial-focus]') || this.dialog;
        focusTarget?.focus({ preventScroll: true });
      });
    }

    close(isVoluntary = false) {
      if (!this.opened) return;
      this.opened = false;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.handleKeydown);

      if (isVoluntary && !this.hasSuccess) {
        this.writeExpiry(DISMISSED_KEY, this.dismissDays);
      }

      if (window.location.hash === RESPONSE_HASH) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }

      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
      window.setTimeout(() => {
        this.hidden = true;
        document.documentElement.classList.remove('noxtek-welcome-popup-open');
        document.body.classList.remove('noxtek-welcome-popup-open');
        this.previousFocus?.focus?.({ preventScroll: true });
      }, delay);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleSubmit(event) {
      if (!this.form?.checkValidity()) return;
      const button = event.submitter || this.submitButton;
      if (!button) return;
      const label = button.querySelector('span');
      button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      if (label && button.dataset.loadingLabel) label.textContent = button.dataset.loadingLabel;
    }

    handleSectionSelect(event) {
      if (!this.designMode || event.target !== this.closest('.shopify-section')) return;
      this.open();
    }

    handleSectionDeselect(event) {
      if (!this.designMode || event.target !== this.closest('.shopify-section')) return;
      this.close(false);
    }
  }

  if (!customElements.get('noxtek-welcome-popup')) {
    customElements.define('noxtek-welcome-popup', NoxTekWelcomePopup);
  }
})();
